/**
 * A draft is an in-progress invoice that a user has started filling out
 * but hasn't yet submitted for import. Any of the human-entered fields
 * can be missing — the user might have only typed a supplier name and
 * walked away — so every domain field is nullable. `id` and `currency`
 * are the only required fields: `id` because the row needs a stable key
 * for React Aria, `currency` because we'd otherwise have nothing to
 * format a partial total against.
 *
 * `lastEditedAt` is *not* a domain field shown in the table; it's kept
 * because future sorting ("most recently edited first") and "unsaved
 * changes since N minutes ago" indicators will read it.
 */
export type ActiveDraft = {
    currency: string;
    date: string | null;
    expensesTotal: number | null;
    expensesTotalTetri: number | null;
    id: string;
    invoiceNumber: string | null;
    lastEditedAt: string;
    linesTotal: number | null;
    linesTotalTetri: number | null;
    productCount: number | null;
    supplier: string | null;
    totalAmount: number | null;
    totalAmountTetri: number | null;
};

/**
 * Display formatter for `invoiceNumber`. Kept local so the widget
 * remains self-contained.
 */
export const formatInvoiceNumber = (invoiceNumber: string): string =>
    `#${invoiceNumber}`;

/**
 * Em-dash placeholder rendered in cells whose backing value is `null`.
 * Centralised so every cell uses the same glyph and a future change
 * (e.g. switch to "—" + tooltip) is one edit.
 */
export const EMPTY_FIELD_PLACEHOLDER = '—';

/**
 * Used by the per-row `aria-label`s on Edit / Delete. Drafts may have
 * no `invoiceNumber` yet, so we fall back to a localised "Untitled
 * draft" string for screen readers and tooltips.
 */
export const getDraftDisplayLabel = (
    draft: ActiveDraft,
    untitledFallback: string,
): string =>
    draft.invoiceNumber !== null
        ? formatInvoiceNumber(draft.invoiceNumber)
        : untitledFallback;

/** Row-level actions surfaced in the table's pinned actions dropdown. */
export type ActiveDraftRowAction = 'edit' | 'delete';

export const ACTIVE_DRAFT_ROW_ACTIONS: {
    id: ActiveDraftRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
}[] = [
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'purchases.widgets.activeDrafts.edit',
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'purchases.widgets.activeDrafts.delete',
        variant: 'danger',
    },
];

/**
 * Null-safe comparators for the drafts table's sortable columns. Every
 * draft field is nullable; these define the *ascending* order with
 * missing values pushed to the end (the DataGrid reverses the result
 * for a descending sort).
 */
export const compareNullableString = (
    a: string | null,
    b: string | null,
): number => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b);
};

export const compareNullableNumber = (
    a: number | null,
    b: number | null,
): number => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
};
